import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo
} from 'react';
import { Control, Controller } from 'react-hook-form';
import type { FieldError } from 'react-hook-form';

/**
 * Интерфейс для элемента метаданных
 */
interface MetadataItem {
  key: string;
  value: string;
}

/**
 * Пропсы компонента MetadataEditor
 */
interface MetadataEditorProps {
  /**
   * Контроллер react-hook-form
   */
  control: Control<any>;
  /**
   * Имя поля в форме
   */
  name: string;
  /**
   * Заголовок секции
   */
  title?: string;
  /**
   * Описание секции
   */
  description?: string;
  /**
   * Состояние отключения
   */
  disabled?: boolean;
}

/**
 * Пропсы внутреннего поля редактора
 */
interface MetadataFieldProps {
  value: Record<string, any> | null | undefined;
  onChange: (value: Record<string, any> | null) => void;
  error?: FieldError | undefined;
  title: string;
  description: string;
  disabled: boolean;
}

/**
 * Поле редактора метаданных
 *
 * Вынесено в отдельный компонент намеренно. Раньше состояние и колбэки жили в
 * MetadataEditor, а useEffect и useMemo вызывались внутри render-колбэка
 * Controller. Колбэк — не компонент: React привязывает объявленные в нём хуки к
 * фиберу Controller, и при изменении числа его рендеров порядок хуков разъедется
 * («Rendered fewer hooks than expected»). Здесь они объявлены в теле настоящего
 * компонента и подчиняются обычным правилам.
 *
 * @param value - Текущее значение поля формы
 * @param onChange - Обработчик изменения значения поля
 * @param error - Ошибка валидации поля
 * @param title - Заголовок секции
 * @param description - Описание секции
 * @param disabled - Состояние отключения
 * @returns JSX элемент
 */
const MetadataField: React.FC<MetadataFieldProps> = ({
  value,
  onChange,
  error,
  title,
  description,
  disabled
}) => {
  const [metadataItems, setMetadataItems] = useState<MetadataItem[]>([]);
  const isUpdatingFromForm = useRef(false);
  const lastFormValue = useRef<Record<string, any> | null | undefined>(
    undefined
  );

  /**
   * Преобразовать объект метаданных в массив элементов
   * @param meta - Объект метаданных
   * @returns Массив элементов метаданных
   */
  const convertMetaToItems = useCallback(
    (meta: Record<string, any> | null | undefined): MetadataItem[] => {
      if (!meta || typeof meta !== 'object') return [];
      return Object.entries(meta).map(([key, value]) => ({
        key,
        value: String(value)
      }));
    },
    []
  );

  /**
   * Преобразовать массив элементов в объект метаданных
   * @param items - Массив элементов метаданных
   * @returns Объект метаданных
   */
  const convertItemsToMeta = useCallback(
    (items: MetadataItem[]): Record<string, any> | null => {
      const validItems = items.filter((item) => item.key.trim() !== '');
      if (validItems.length === 0) return null;

      const meta: Record<string, any> = {};
      validItems.forEach((item) => {
        meta[item.key.trim()] = item.value;
      });
      return meta;
    },
    []
  );

  /**
   * Добавить новый элемент метаданных
   */
  const addMetadataItem = useCallback(() => {
    setMetadataItems((prev) => [...prev, { key: '', value: '' }]);
  }, []);

  /**
   * Удалить элемент метаданных
   * @param index - Индекс элемента для удаления
   */
  const removeMetadataItem = useCallback((index: number) => {
    setMetadataItems((prev) => prev.filter((_, i) => i !== index));
  }, []);

  /**
   * Обновить ключ элемента метаданных
   * @param index - Индекс элемента
   * @param newKey - Новый ключ
   */
  const updateMetadataKey = useCallback((index: number, newKey: string) => {
    setMetadataItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, key: newKey } : item))
    );
  }, []);

  /**
   * Обновить значение элемента метаданных
   * @param index - Индекс элемента
   * @param newValue - Новое значение
   */
  const updateMetadataValue = useCallback((index: number, newValue: string) => {
    setMetadataItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, value: newValue } : item))
    );
  }, []);

  /**
   * Проверить уникальность ключей
   * @param items - Массив элементов метаданных
   * @returns Массив дублированных ключей
   */
  const getDuplicateKeys = useCallback((items: MetadataItem[]): string[] => {
    const keys = items
      .map((item) => item.key.trim())
      .filter((key) => key !== '');
    const duplicates = keys.filter((key, index) => keys.indexOf(key) !== index);
    return [...new Set(duplicates)];
  }, []);

  // Синхронизация внутреннего состояния с полем формы
  // Обновляем только если значение действительно изменилось
  useEffect(() => {
    if (!isUpdatingFromForm.current && lastFormValue.current !== value) {
      lastFormValue.current = value;
      const items = convertMetaToItems(value);
      setMetadataItems(items);
    }
  }, [value, convertMetaToItems]);

  // Обновление поля формы при изменении внутреннего состояния
  // Используем ref для предотвращения циклических обновлений
  useEffect(() => {
    if (!isUpdatingFromForm.current) {
      isUpdatingFromForm.current = true;
      const meta = convertItemsToMeta(metadataItems);

      // Обновляем форму только если значение действительно изменилось
      if (JSON.stringify(meta) !== JSON.stringify(lastFormValue.current)) {
        lastFormValue.current = meta;
        onChange(meta);
      }

      // Сбрасываем флаг синхронно
      isUpdatingFromForm.current = false;
    }
  }, [metadataItems, convertItemsToMeta, onChange]);

  const duplicateKeys = useMemo(
    () => getDuplicateKeys(metadataItems),
    [metadataItems, getDuplicateKeys]
  );

  return (
    <div className="form-control">
      <label className="label">
        <span className="label-text font-medium">{title}</span>
      </label>
      {description && (
        <label className="label">
          <span className="label-text-alt text-base-content/60">
            {description}
          </span>
        </label>
      )}

      <div className="space-y-3">
        {metadataItems.length === 0 ? (
          <div className="text-center py-4 text-base-content/60">
            <i className="fas fa-tags text-2xl mb-2"></i>
            <p>Нет метаданных</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table table-zebra w-full">
              <thead>
                <tr>
                  <th>Ключ</th>
                  <th>Значение</th>
                  <th className="w-16">Действия</th>
                </tr>
              </thead>
              <tbody>
                {metadataItems.map((item, index) => {
                  const isDuplicateKey =
                    duplicateKeys.includes(item.key.trim()) &&
                    item.key.trim() !== '';
                  const isEmptyKey = item.key.trim() === '';

                  return (
                    <tr key={index}>
                      <td>
                        <input
                          type="text"
                          value={item.key}
                          onChange={(e) =>
                            updateMetadataKey(index, e.target.value)
                          }
                          className={`input input-bordered input-sm w-full ${
                            isDuplicateKey || isEmptyKey ? 'input-error' : ''
                          }`}
                          placeholder="Ключ"
                          disabled={disabled}
                        />
                        {isDuplicateKey && (
                          <div className="text-error text-xs mt-1">
                            Ключ должен быть уникальным
                          </div>
                        )}
                        {isEmptyKey && item.value && (
                          <div className="text-error text-xs mt-1">
                            Ключ не может быть пустым
                          </div>
                        )}
                      </td>
                      <td>
                        <input
                          type="text"
                          value={item.value}
                          onChange={(e) =>
                            updateMetadataValue(index, e.target.value)
                          }
                          className="input input-bordered input-sm w-full"
                          placeholder="Значение"
                          disabled={disabled}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          onClick={() => removeMetadataItem(index)}
                          className="btn btn-ghost btn-sm text-error hover:bg-error/10"
                          disabled={disabled}
                          title="Удалить"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <button
          type="button"
          onClick={addMetadataItem}
          className="btn btn-outline btn-sm"
          disabled={disabled}
        >
          <i className="fas fa-plus mr-2"></i>
          Добавить метаданные
        </button>
      </div>

      {error && (
        <label className="label">
          <span className="label-text-alt text-error">{error.message}</span>
        </label>
      )}
    </div>
  );
};

/**
 * Компонент редактора метаданных
 * Позволяет редактировать JSON-данные в виде таблицы ключ-значение
 *
 * @param control - Контроллер react-hook-form
 * @param name - Имя поля в форме
 * @param title - Заголовок секции
 * @param description - Описание секции
 * @param disabled - Состояние отключения
 * @returns JSX элемент
 */
export const MetadataEditor: React.FC<MetadataEditorProps> = ({
  control,
  name,
  title = 'Метаданные',
  description = 'Дополнительные атрибуты в формате ключ-значение',
  disabled = false
}) => (
  <Controller
    name={name}
    control={control}
    render={({ field: { value, onChange }, fieldState: { error } }) => (
      <MetadataField
        value={value}
        onChange={onChange}
        error={error}
        title={title}
        description={description}
        disabled={disabled}
      />
    )}
  />
);
